import React from 'react';
import styled from '@emotion/styled';
import { CheckCircle2, ArrowRight } from 'lucide-react';
import ChoiceGroup from './questions/ChoiceGroup';
import StanceItem, { STANCES } from './questions/StanceItem';
import SpreadBar from './questions/SpreadBar';
import { useTranslation } from '../../hooks/useTranslation';

/**
 * Kysely — the first pre-work task as a form, and after submitting, as the
 * room's spread with the person's answers marked (specs/me-account.md,
 * specs/modules/ennakkotehtavat/README.md). Pure: items, answers and results
 * come in as props; the container (KyselyTask) fetches and posts. Four
 * sections, the same for everyone: claims about today, three sharper claims
 * as stances, the target's requirements, and one pick for where the
 * workshop ends.
 */
const Wrap = styled.div`display: flex; flex-direction: column; gap: 1.4rem;`;
const Section = styled.section`display: flex; flex-direction: column; gap: 0.6rem;`;
const Eyebrow = styled.div`font-size: 0.72rem; letter-spacing: 0.1em; text-transform: uppercase; color: #7b8794;`;
const Q = styled.div`font-weight: 600; color: #e8e8ef; line-height: 1.4;`;
const Intro = styled.p`margin: 0; color: #9aa4b2; line-height: 1.55;`;
const Submit = styled.button`
  all: unset; cursor: pointer; align-self: flex-start; display: inline-flex; align-items: center; gap: 0.5rem;
  padding: 0.75rem 1.2rem; border-radius: 10px; font-weight: 600;
  background: rgba(91, 157, 217, 0.18); border: 1px solid rgba(142, 193, 240, 0.6); color: #cfe6ff;
  &:hover, &:focus-visible { background: rgba(91, 157, 217, 0.28); }
  &:disabled { cursor: default; opacity: 0.5; }
`;
const Err = styled.p`margin: 0; color: #FF8A85; font-size: 0.88rem;`;
const Done = styled.div`display: flex; align-items: center; gap: 0.5rem; color: #6fd3c8; font-weight: 600;`;
const Card = styled.div`background: #141821; border: 1px solid #232a36; border-radius: 10px; padding: 1rem 1.1rem;`;

export const EMPTY_ANSWERS = { claims: [], stance: {}, sockets: [], expect: null };

/**
 * @param {object} items    { claims[], stance[], sockets[], expect[] } options with id, text, source
 * @param {object} answers  { claims: string[], stance: {id: stance}, sockets: string[], expect: string|null }
 * @param {boolean} submitted
 * @param {object|null} own    the stored answers when submitted (same shape, expect as string[])
 * @param {object|null} spread the room's counts per option, with `participants`
 */
export default function Kysely({ items, answers = EMPTY_ANSWERS, onChange, onSubmit, submitted = false, own = null, spread = null, busy = false, error = null, submittedAt = null }) {
  const { t } = useTranslation();
  const labels = { agree: t('me.kysely.agree'), unsure: t('me.kysely.unsure'), disagree: t('me.kysely.disagree') };
  if (!items) return <Intro>{t('me.loading')}</Intro>;
  if (!items.total) return <Intro>{t('me.kysely.notReady')}</Intro>;

  if (submitted) {
    const n = spread?.participants || 0;
    const foot = t('me.kysely.answered', { count: n });
    const stanceOpts = (row) => STANCES.map((s) => ({ id: s.id, text: labels[s.id], count: row?.[s.id] || 0, color: s.color }));
    return (
      <Wrap>
        <Done><CheckCircle2 size={18} /> {t('me.kysely.done')}{submittedAt ? ` · ${new Date(submittedAt).toLocaleDateString('fi-FI')}` : ''}</Done>
        <Intro>{t('me.kysely.resultsIntro')}</Intro>
        <Card><SpreadBar title={t('me.kysely.claimsQ')} options={items.claims.map((o) => ({ ...o, count: spread?.claims.find((c) => c.id === o.id)?.agree || 0 }))} total={n} own={own?.claims || []} footer={foot} /></Card>
        {items.stance.map((o) => (
          <Card key={o.id}><SpreadBar title={o.text} options={stanceOpts(spread?.stance.find((s) => s.id === o.id))} total={n} own={own?.stance?.[o.id] ? [own.stance[o.id]] : []} /></Card>
        ))}
        <Card><SpreadBar title={t('me.kysely.socketsQ')} options={items.sockets.map((o) => ({ ...o, count: spread?.sockets.find((c) => c.id === o.id)?.agree || 0 }))} total={n} own={own?.sockets || []} /></Card>
        <Card><SpreadBar title={t('me.kysely.expectQ')} options={items.expect.map((o) => ({ ...o, count: spread?.expect.find((c) => c.id === o.id)?.pick || 0 }))} total={n} own={own?.expect || []} footer={t('me.kysely.expectFoot')} /></Card>
      </Wrap>
    );
  }

  const set = (patch) => onChange?.({ ...answers, ...patch });
  const stanceComplete = items.stance.every((o) => ['agree', 'unsure', 'disagree'].includes(answers.stance?.[o.id]));
  return (
    <Wrap>
      <Intro>{t('me.kysely.intro')}</Intro>
      <Section>
        <Eyebrow>{t('me.kysely.part', { n: 1, of: 4 })}</Eyebrow>
        <ChoiceGroup question={t('me.kysely.claimsQ')} hint={t('me.kysely.claimsHint')} options={items.claims} values={answers.claims} onChange={(v) => set({ claims: v })} mode="multi" disabled={busy} />
      </Section>
      <Section>
        <Eyebrow>{t('me.kysely.part', { n: 2, of: 4 })}</Eyebrow>
        <Q>{t('me.kysely.stanceQ')}</Q>
        {items.stance.map((o) => (
          <StanceItem key={o.id} text={o.text} value={answers.stance?.[o.id] || null} labels={labels} disabled={busy}
            onChange={(v) => set({ stance: { ...answers.stance, [o.id]: v } })} />
        ))}
      </Section>
      <Section>
        <Eyebrow>{t('me.kysely.part', { n: 3, of: 4 })}</Eyebrow>
        <ChoiceGroup question={t('me.kysely.socketsQ')} hint={t('me.kysely.socketsHint')} options={items.sockets} values={answers.sockets} onChange={(v) => set({ sockets: v })} mode="multi" disabled={busy} />
      </Section>
      <Section>
        <Eyebrow>{t('me.kysely.part', { n: 4, of: 4 })}</Eyebrow>
        <ChoiceGroup question={t('me.kysely.expectQ')} hint={t('me.kysely.expectHint')} options={items.expect} values={answers.expect ? [answers.expect] : []} onChange={(v) => set({ expect: v[0] || null })} mode="single" disabled={busy} />
      </Section>
      {error && <Err>{error}</Err>}
      <Submit type="button" disabled={busy || !stanceComplete} onClick={onSubmit}>{t('me.kysely.submit')} <ArrowRight size={16} /></Submit>
      <Intro>{t('me.kysely.finalNote')}</Intro>
    </Wrap>
  );
}
